/**
 * Contract types for the resonance-server REST/SSE API.
 *
 * These mirror the server's serializers, not a schema generator — resonance-server
 * hand-rolls its JSON. When a server response shape changes, change it here. The
 * authoritative sources are noted per group.
 */

/* ---------- Auth (modules/auth/auth.routes.ts) ---------- */

export interface TokenPair {
  accessToken: { token: string; expiresAt: string };
  refreshToken: { token: string; expiresAt: string };
}

export interface AuthSession {
  tokens: TokenPair;
  bootstrap: Bootstrap;
}

export interface GoogleStart {
  authUrl: string;
  /**
   * Where the server will redirect after Google returns, with `?code=`
   * appended — echoes back the callback the client requested.
   */
  callbackUrl: string;
}

/* ---------- Bootstrap (modules/bootstrap/bootstrap.service.ts) ---------- */

export interface Bootstrap {
  authenticated: boolean;
  workspace: { id: string; displayName: string; role: string };
  user?: { id: string; firstName: string; lastName: string | null; email: string };
  model: { id: string; displayName: string };
  theme: ThemePreference;
  navigationSections: NavigationSection[];
  crmCatalog: { metadataVersion: number; objects: CatalogObject[] };
}

export interface ThemePreference {
  appearance: 'system' | 'dark' | 'light';
  themeColor: 'default' | 'blue' | 'purple' | 'red' | 'pink' | 'orange' | 'yellow' | 'teal';
}

/* ---------- Settings (modules/settings/settings.routes.ts) ---------- */

export interface Personalization {
  personalization: string;
  goals: string;
  currentMission: string | null;
  nextStepHint: string | null;
  missionSource: string | null;
  missionUpdatedAt: string | null;
  updatedAt: string | null;
  success?: boolean;
  learning?: { pending: number; failed: number; latest: { id: string; status: string; summary: string | null } | null };
  progress?: { revision: string; pending: number; lastReviewedAt: string | null;
    document: { mission: { id: string; title: string; status: 'active'|'blocked'|'completed'|'superseded'; blockers: string[] } | null; reason: string } };
}

export interface BillingUsage {
  creditBalance: {
    current: number;
    allocated: number;
    used: number;
    remaining: number;
  };
  subscription: {
    tier: string;
    status: string;
    periodStart: string | null;
    periodEnd: string | null;
    daysRemaining: number;
  };
  usage: {
    compute: UsageCategory;
    enhancements: UsageCategory;
    total: UsageCategory;
  };
  limits: {
    daily: UsageLimit;
    monthly: UsageLimit;
  };
  alerts: Array<{ type: string; code: string; message: string }>;
  tokenUsage: (TokenUsageTotals & {
    periodStart: string;
    periodEnd: string;
    bySource: Record<TokenUsageSource, TokenUsageTotals>;
    recentRequests: TokenUsageRequest[];
    recentRequestLimit: number;
  }) | null;
}

export type TokenUsageSource = 'chat' | 'auto' | 'other';

export interface TokenCounts {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  totalInputTokens: number;
  totalTokens: number;
  cacheReadPercentage: number;
}

export interface TokenUsageTotals extends TokenCounts {
  requests: number;
  webSearchRequests: number;
  serverToolCostUsd: number;
  estimatedCostUsd: number;
}

export interface TokenUsageRequest extends TokenCounts {
  id: string;
  source: TokenUsageSource;
  purpose?: string | null;
  taskId?: string | null;
  conversationId: string | null;
  conversationTitle: string | null;
  model: string;
  provider: string;
  webSearchRequests: number;
  estimatedCostUsd: number;
  usedPersonalKey: boolean;
  createdAt: string;
}

export interface UsageCategory {
  credits: number;
  percentage: number;
}

export interface UsageLimit {
  used: number;
  limit: number | null;
  percentage: number | null;
}

export interface NavigationSection {
  id: string;
  title: string;
  items: Array<{
    id: string;
    label: string;
    icon: string;
    path: string;
    objectNamePlural?: string;
    destination?: string;
  }>;
}

export interface CatalogObject {
  id: string;
  nameSingular: string;
  namePlural: string;
  labelSingular: string;
  labelPlural: string;
  description: string | null;
  icon: string | null;
  iconKey: string;
  isCustom: boolean;
  isRemote: boolean;
  fields: CatalogField[];
  defaultView: unknown | null;
}

export interface CatalogField {
  id: string;
  type: FieldType;
  name: string;
  label: string;
  icon: string | null;
  options: Array<{ value: string; label: string; color?: string }> | null;
}

/* ---------- Schema descriptor (modules/schema/schema.service.ts) ---------- */

export type FieldType =
  | 'text' | 'markdown' | 'number' | 'boolean' | 'date' | 'datetime'
  | 'select' | 'multiSelect' | 'currency' | 'url' | 'emails'
  | 'phones' | 'address' | 'rating' | 'relation' | 'position' | 'actor';

export interface SchemaDescriptor {
  schemaVersion: number;
  objects: SchemaObject[];
}

export interface IndexSort { field: string; direction: 'asc' | 'desc' }
export interface IndexPreferences { showContactOptions?: boolean; detailFields?: string[]; fields?: string[]; mode?: 'table' | 'board'; sort?: IndexSort | null }

export interface SchemaObject {
  indexPreferences?: IndexPreferences;
  id: string;
  name: string;
  namePlural: string;
  label: string;
  labelPlural: string;
  icon: string | null;
  description: string | null;
  isSystem: boolean;
  labelField: string;
  position: number;
  recordCount: number;
  fields: SchemaField[];
  views: SchemaView[];
}

export interface SchemaField {
  id: string;
  name: string;
  label: string;
  type: FieldType;
  description: string | null;
  icon: string | null;
  options: Record<string, unknown>;
  relation: Record<string, unknown> | null;
  isSystem: boolean;
  isRequired: boolean;
  position: number;
}

export interface SchemaView {
  id: string;
  name: string;
  icon: string | null;
  type: 'table' | 'board';
  config: ViewConfig;
  position: number;
}

export interface ViewConfig {
  fields?: string[];
  groupBy?: string;
  groupByField?: string;
  columns?: Array<{ field: string; visible?: boolean }>;
  sorts?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  [key: string]: unknown;
}

/* ---------- Records (modules/records/record-read.service.ts) ---------- */

export interface ObjectSummary {
  id: string;
  nameSingular: string;
  namePlural: string;
  labelSingular: string;
  labelPlural: string;
  iconKey: string;
}

export interface RecordReference {
  id: string;
  objectMetadataId: string;
  objectNamePlural: string;
  objectLabelSingular: string;
  label: string;
}

export interface RecordFieldValue {
  id: string;
  name: string;
  label: string;
  type: FieldType;
  icon: string | null;
  value: unknown;
  displayValue: string;
  relationReferences: RecordReference[];
}

export interface CrmRecord {
  id: string;
  label: string;
  displayName: string;
  imageUrl: string | null;
  data: Record<string, unknown>;
  position: string | null;
  createdAt: string;
  updatedAt: string;
  createdByActorId: string;
  updatedByActorId: string;
  fields: RecordFieldValue[];
  relations: RecordReference[];
}

export interface RecordListResponse {
  object: ObjectSummary;
  records: CrmRecord[];
  totalCount: number;
  limit: number;
  offset: number;
}

export interface ContactOption {
  sourceRecordId: string; sourceRecordLabel: string; fieldId: string; label: string;
}
export interface CallOption extends ContactOption { number: string; dialNumber: string }
export interface EmailOption extends ContactOption { address: string }
export interface RecordDetailResponse {
  callOptions?: CallOption[];
  emailOptions?: EmailOption[];
  object: ObjectSummary;
  record: CrmRecord;
  associationTabs: unknown[];
}

/* ---------- Query AST (modules/records/record.routes.ts) ---------- */

export type FilterOp =
  | 'eq' | 'neq' | 'contains' | 'gt' | 'gte'
  | 'lt' | 'lte' | 'in' | 'isNull' | 'linkedTo';

export interface RecordQuery {
  filters?: Array<{ field: string; op: FilterOp; value?: unknown }>;
  sorts?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  aggregate?: { op: 'count' | 'sum' | 'avg'; field?: string; groupBy?: string };
  cursor?: string;
  limit?: number;
  offset?: number;
}

/* ---------- Search & timeline (modules/activity/activity.routes.ts) ---------- */

export interface SearchResult {
  id: string;
  objectName: string;
  objectLabel: string;
  displayName: string;
  snippet: string;
  rank: number;
}

export type ActorKind = 'user' | 'echo' | 'workflow' | 'system';

export interface TimelineActor {
  id: string;
  kind: ActorKind;
  displayName: string;
}

export interface TimelineEvent {
  id: number;
  actor: TimelineActor;
  verb: 'created' | 'updated' | 'deleted' | 'restored' | 'linked' | 'unlinked' | 'call_initiated';
  communication?: {
    sourceRecordLabel: string;
    contextRecordLabel: string;
    sourceRecordId: string;
    contextRecordId: string;
    number: string;
    occurredAt: string;
  } | null;
  recordId: string;
  objectDefId: string;
  changes: Record<string, { from: unknown; to: unknown }>;
  snapshot: Record<string, unknown> | null;
  provenance: {
    conversationId?: string;
    messageId?: string;
    toolCallId?: string;
    actionIntentId?: string;
    workOrderId?: string;
    workflowRunId?: string;
  };
  createdAt: string;
}

/** GET /v1/context/record/:id */
export interface RecordContext {
  record: {
    id: string; objectName: string; objectLabel: string;
    displayName: string; data: Record<string, unknown>;
    createdAt: string; updatedAt: string;
  };
  linkedRecords: Array<{ id: string; displayName: string; objectName: string; direction: string }>;
  timeline: TimelineEvent[];
  tasks: Array<Record<string, unknown>>;
  notes: Array<Record<string, unknown>>;
}

/* ---------- Chat (modules/chat/conversation.service.ts) ---------- */

export type ModelSelection = 'auto' | 'haiku' | 'sonnet' | 'opus' | 'fable' | 'gpt-6.1-sol' | 'gpt-6-astra' | 'gpt-6-luna';

export function isModelSelection(value: unknown): value is ModelSelection {
  return value === 'auto' || value === 'gpt-6.1-sol' || value === 'gpt-6-astra' || value === 'gpt-6-luna' || value === 'haiku' || value === 'sonnet' || value === 'opus' || value === 'fable';
}

export interface AgentPart {
  id?: string;
  type: 'text' | 'thinking' | 'citation' | 'tool_call' | 'tool_result';
  text?: string;
  toolId?: string;
  toolName?: string;
  input?: unknown;
  output?: unknown;
  error?: string;
  status?: 'complete' | 'failed' | 'running' | 'cancelled';
  citation?: {
    type: string;
    url: string;
    title?: string;
    citedText?: string;
  };
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  parts: AgentPart[];
  createdAt: string;
  status?: 'streaming' | 'complete' | 'failed' | 'cancelled';
  learningId?: string;
}

export interface HomeContextSnapshot {
  goals: string;
  currentMission: string | null;
  nextStepHint: string | null;
  capturedAt: string;
}

export interface Conversation {
  id: string;
  title: string;
  model: ModelSelection;
  workspaceId: string;
  createdAt: string;
  updatedAt: string;
  messages: ChatMessage[];
  homeContext?: HomeContextSnapshot | null;
  [key: string]: unknown;
}

export interface ConversationSummary {
  id: string;
  title: string;
  model: string;
  lastActivityAt: string | null;
  preview: string | null;
  messageCount: number;
  isStreaming: boolean;
}

export interface PageContext {
  path: string;
  route?: string;
  objectName?: string;
  recordId?: string;
  viewId?: string;
  filters?: unknown[];
  sorts?: unknown[];
  pageSpecificContext?: Record<string, unknown>;
}

/* ---------- SSE stream events (modules/chat/chat.routes.ts) ---------- */

export type StreamEvent =
  | { event: 'message_start'; data: { messageId: string } }
  | { event: 'text_delta'; data: { messageId: string; delta: string } }
  | { event: 'thinking_delta'; data: { messageId: string; delta: string } }
  | {
      event: 'citation';
      data: { messageId: string; citation: NonNullable<AgentPart['citation']> };
    }
  | {
      event: 'tool_call';
      data: { messageId: string; toolId: string; toolName: string; input: unknown; status: string };
    }
  | {
      event: 'tool_result';
      data: {
        messageId: string; toolId: string; toolName: string;
        success: boolean; output?: unknown; error?: string; status: string;
      };
    }
  | {
      event: 'message_end';
      data: {
        messageId?: string;
        status: 'success' | 'error' | 'cancelled';
        error?: string;
        conversation?: Conversation;
      };
    };

/* ---------- Proactive (modules/proactive/proactive.routes.ts) ---------- */

/** Written by the brief generator in jobs/job-runtime.ts. */
export interface BriefContent {
  summary?: string;
  accomplishments?: string[];
  insights?: string[];
  next_steps?: string[];
  channelSignals?: Array<Record<string, unknown>>;
}

export interface DailyBrief {
  enabled: boolean;
  has_unseen: boolean;
  brief: { revision: number; content: BriefContent; generated_at: string } | null;
  action_intents: ActionIntent[];
  is_empty: boolean;
}

export interface ActionIntent {
  id: string;
  tool_name: string;
  status: 'pending_approval' | 'rejected' | 'executing' | 'completed' | 'failed';
  payload: Record<string, unknown>;
  summary: string | null;
  desired_execute_at: string | null;
  created_at: string;
}

export interface StagedImportSheet {
  artifact_id: string;
  sheet?: string;
  columns: string[];
  total_rows: number;
}

export interface StagedImport {
  artifact_id: string;
  conversation_id: string;
  columns: string[];
  total_rows: number;
  sample_rows: Array<Record<string, string>>;
  sheet?: string;
  additional_sheets?: StagedImportSheet[];
}

export interface Favorite {
  id: string;
  recordId: string;
  position: number;
  displayName: string;
  objectName: string;
  objectNamePlural: string;
  objectLabel: string;
  iconKey: string;
}
